import { ColumnConfig, numberSort, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { JSX, useEffect, useState } from "react";
import { ZipSummariesAPIResponseModel, ZipSummariesTableData } from "./interfaces";
import { TemplateMetadata } from "@app/templates";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";

interface ZipSummariesProps {
  bulkActionBar: JSX.Element;
  closestProviderCount: number;
  compareToZipSummaries?: ZipSummariesAPIResponseModel[];
  loading: boolean;
  resultsForComparison?: string;
  resultsForSource?: string;
  showAvgDist: boolean;
  showAvgTime: boolean;
  showWithAndWithoutTimeDistanceValues: boolean;
  isTimeOnlyCalculationMethod: boolean;
  isDistanceOnlyCalculationMethod: boolean;
  templateMetadata: TemplateMetadata;
  zipSummaries: ZipSummariesAPIResponseModel[];
}

const ZipSummariesPanel: React.FC<React.PropsWithChildren<ZipSummariesProps>> = (props: ZipSummariesProps) => {
  const [tableData, setTableData] = useState<ZipSummariesTableData[]>([]);
  const [comparedTableData, setComparedTableData] = useState<ZipSummariesTableData[]>(null);

  useEffect(() => {
    let newTableData = buildTableData(props.zipSummaries, props.resultsForSource);
    let newComparedTableData = buildTableData(props.compareToZipSummaries, props.resultsForComparison);

    // if the selected timeslice has zip summaries disabled but the compared one has them enabled
    if (newTableData.length === 0 && newComparedTableData && newComparedTableData.length > 0) {
      newTableData = populateWithBlankRows(newComparedTableData);
      // vice versa - !!comparedData will be false if undefined, but true if []
    } else if (newTableData.length > 0 && !!newComparedTableData && newComparedTableData.length === 0) {
      newComparedTableData = populateWithBlankRows(newTableData);
    }

    setTableData(newTableData);
    setComparedTableData(newComparedTableData);
  }, []);

  const generateColumnConfig = () => {
    const columns: ColumnConfig = {
      avgDist: {
        displayText: props.closestProviderCount > 1 ? "Avg Dist 1" : "Avg Dist",
        hidden: !props.showAvgDist,
        testId: "avg-dist",
      },
      avgDist2: {
        displayText: "Avg Dist 2",
        hidden: !(props.showAvgDist && (props.zipSummaries.some((z) => !!z.averageDistance2) || (!!props.compareToZipSummaries && props.compareToZipSummaries.some((z) => !!z.averageDistance2)))),
        testId: "avg-dist-2",
      },
      avgDist2With: {
        displayText: "Avg Dist 2 With Access",
        hidden: !props.showAvgDist || props.templateMetadata.closestProviderCount !== 2,
        testId: "access-avg-dist2-with",
      },
      avgDist2Without: {
        displayText: "Avg Dist 2 Without Access",
        hidden: !props.showAvgDist || props.templateMetadata.closestProviderCount !== 2,
        testId: "access-avg-dist2-without",
      },
      avgDistWith: {
        displayText: props.templateMetadata.closestProviderCount > 1 ? "Avg Dist 1 With Access" : "Avg Dist With Access",
        hidden: !props.showAvgDist,
        testId: "access-avg-dist-with",
      },
      avgDistWithout: {
        displayText: props.templateMetadata.closestProviderCount > 1 ? "Avg Dist 1 Without Access" : "Avg Dist Without Access",
        hidden: !props.showAvgDist,
        testId: "access-avg-dist-without",
      },
      avgTime: {
        displayText: props.closestProviderCount > 1 ? "Avg Time 1" : "Avg Time",
        hidden: !props.showAvgTime,
        testId: "avg-time",
      },
      avgTime2: {
        displayText: "Avg Time 2",
        hidden: !(props.showAvgTime && (props.zipSummaries.some((z) => !!z.averageTime2) || (!!props.compareToZipSummaries && props.compareToZipSummaries.some((z) => !!z.averageTime2)))),
        testId: "avg-time-2",
      },
      avgTime2With: {
        displayText: "Avg Time 2 With Access",
        hidden: !props.showAvgTime || props.templateMetadata.closestProviderCount !== 2,
        testId: "access-avg-time2-with",
      },
      avgTime2Without: {
        displayText: "Avg Time 2 Without Access",
        hidden: !props.showAvgTime || props.templateMetadata.closestProviderCount !== 2,
        testId: "access-avg-time2-without",
      },
      avgTimeWith: {
        displayText: props.templateMetadata.closestProviderCount > 1 ? "Avg Time 1 With Access" : "Avg Time With Access",
        hidden: !props.showAvgTime,
        testId: "access-avg-time-with",
      },
      avgTimeWithout: {
        displayText: props.templateMetadata.closestProviderCount > 1 ? "Avg Time 1 Without Access" : "Avg Time Without Access",
        hidden: !props.showAvgTime,
        testId: "access-avg-time-without",
      },
      city: {
        displayText: "City",
        sticky: true,
        testId: "city",
      },
      inRegionZipProviderCount: {
        displayText: "Provider Count",
        testId: "provider-count",
      },
      key: {
        displayText: "",
        hidden: true,
        testId: "",
      },
      maxDist: {
        displayText: props.closestProviderCount > 1 ? "Max Dist 1" : "Max Dist",
        hidden: !props.showAvgDist,
        testId: "max-dist",
      },
      maxDist2: {
        displayText: "Max Dist 2",
        hidden: !(props.showAvgDist && (props.zipSummaries.some((z) => !!z.maximumDistance2) || (!!props.compareToZipSummaries && props.compareToZipSummaries.some((z) => !!z.maximumDistance2)))),
        testId: "max-dist-2",
      },
      maxDist2With: {
        displayText: "Max Dist 2 With Access",
        hidden: !props.showAvgDist || props.templateMetadata.closestProviderCount !== 2,
        testId: "access-max-dist2-with",
      },
      maxDist2Without: {
        displayText: "Max Dist 2 Without Access",
        hidden: !props.showAvgDist || props.templateMetadata.closestProviderCount !== 2,
        testId: "access-max-dist2-without",
      },
      maxDistWith: {
        displayText: props.closestProviderCount > 1 ? "Max Dist 1 With Access" : "Max Dist With Access",
        hidden: !props.showAvgDist,
        testId: "access-max-dist-with",
      },
      maxDistWithout: {
        displayText: props.closestProviderCount > 1 ? "Max Dist 1 Without Access" : "Max Dist Without Access",
        hidden: !props.showAvgDist,
        testId: "access-max-dist-without",
      },
      maxTime: {
        displayText: props.closestProviderCount > 1 ? "Max Time 1" : "Max Time",
        hidden: !props.showAvgTime,
        testId: "max-time",
      },
      maxTime2: {
        displayText: "Max Time 2",
        hidden: !(props.showAvgTime && (props.zipSummaries.some((z) => !!z.maximumTime2) || (!!props.compareToZipSummaries && props.compareToZipSummaries.some((z) => !!z.maximumTime2)))),
        testId: "max-time-2",
      },
      maxTime2With: {
        displayText: "Max Time 2 With Access",
        hidden: !props.showAvgTime || props.templateMetadata.closestProviderCount !== 2,
        testId: "access-max-time2-with",
      },
      maxTime2Without: {
        displayText: "Max Time 2 Without Access",
        hidden: !props.showAvgTime || props.templateMetadata.closestProviderCount !== 2,
        testId: "access-max-time2-without",
      },
      maxTimeWith: {
        displayText: props.closestProviderCount > 1 ? "Max Time 1 With Access" : "Max Time With Access",
        hidden: !props.showAvgTime,
        testId: "access-max-time-with",
      },
      maxTimeWithout: {
        displayText: props.closestProviderCount > 1 ? "Max Time 1 Without Access" : "Max Time Without Access",
        hidden: !props.showAvgTime,
        testId: "access-max-time-without",
      },
      membershipCount: {
        displayText: "Membership Count",
        testId: "membership-count",
      },
      membersWithAccessCount: {
        displayText: "# With",
        testId: "membership-count-with-access",
      },
      membersWithAccessPercent: {
        displayText: "With %",
        testId: "member-access-percent",
      },
      membersWithoutAccessCount: {
        displayText: "# Without",
        testId: "membership-count-without-access",
      },
      membersWithoutAccessPercent: {
        displayText: "Without %",
        testId: "member-without-access-percent",
      },
      resultsFor: {
        displayText: "Results For",
        hidden: !props.resultsForComparison || !props.resultsForSource,
        noSort: true,
        testId: "results-for",
      },
      zipCode: {
        displayText: "Zip Code",
        sticky: true,
        testId: "zip-code",
      }
    };

    return columns;
  }

  const buildTableData: (response: ZipSummariesAPIResponseModel[], resultsForValue?: string) => ZipSummariesTableData[] = (response: ZipSummariesAPIResponseModel[], resultsForValue?: string) => {
    // This check relies on a compared-but-uncalculated adequacy results response sending [], as opposed to undefined
    if (!response) {
      return undefined;
    }
    /* oxlint-disable sort-keys */
    const formattedData: ZipSummariesTableData[] = response.map((d) => ({
      key: d.zipCode,
      zipCode: d.zipCode,
      resultsFor: !!resultsForValue ? resultsForValue : "---",
      city: d.city,
      inRegionZipProviderCount: d.inRegionZipProviderCount ? d.inRegionZipProviderCount.toLocaleString("en-US") : "---",
      membershipCount: d.memberCount.toLocaleString("en-US"),
      membersWithAccessCount: d.membersWithAccessCount.toLocaleString("en-US"),
      membersWithAccessPercent: d.memberAccessPercentage.toFixed(1),
      membersWithoutAccessCount: d.membersWithoutAccessCount.toLocaleString("en-US"),
      membersWithoutAccessPercent: `${(100 - d.memberAccessPercentage).toFixed(1)}`,
      avgDist: d.averageDistance && !props.isTimeOnlyCalculationMethod ? d.averageDistance.toFixed(1) : "---",
      avgDistWith: d.averageDistanceWithAccess && props.showWithAndWithoutTimeDistanceValues && !props.isTimeOnlyCalculationMethod ? d.averageDistanceWithAccess.toFixed(1) : "---",
      avgDistWithout: d.averageDistanceWithoutAccess && props.showWithAndWithoutTimeDistanceValues && !props.isTimeOnlyCalculationMethod ? d.averageDistanceWithoutAccess.toFixed(1) : "---",
      avgDist2: d.averageDistance2 && !props.isTimeOnlyCalculationMethod ? d.averageDistance2.toFixed(1) : "---",
      avgDist2With: d.averageDistance2WithAccess && props.showWithAndWithoutTimeDistanceValues && !props.isTimeOnlyCalculationMethod ? d.averageDistance2WithAccess.toFixed(1) : "---",
      avgDist2Without: d.averageDistance2WithoutAccess && props.showWithAndWithoutTimeDistanceValues && !props.isTimeOnlyCalculationMethod ? d.averageDistance2WithoutAccess.toFixed(1) : "---",
      maxDist: d.maximumDistance && !props.isTimeOnlyCalculationMethod ? d.maximumDistance.toFixed(1) : "---",
      maxDistWith: d.maxDistanceWithAccess && props.showWithAndWithoutTimeDistanceValues && !props.isTimeOnlyCalculationMethod ? d.maxDistanceWithAccess.toFixed(1) : "---",
      maxDistWithout: d.maxDistanceWithoutAccess && props.showWithAndWithoutTimeDistanceValues && !props.isTimeOnlyCalculationMethod ? d.maxDistanceWithoutAccess.toFixed(1) : "---",
      maxDist2: d.maximumDistance2 && !props.isTimeOnlyCalculationMethod ? d.maximumDistance2.toFixed(1) : "---",
      maxDist2With: d.maxDistance2WithAccess && props.showWithAndWithoutTimeDistanceValues && !props.isTimeOnlyCalculationMethod ? d.maxDistance2WithAccess.toFixed(1) : "---",
      maxDist2Without: d.maxDistance2WithoutAccess && props.showWithAndWithoutTimeDistanceValues && !props.isTimeOnlyCalculationMethod ? d.maxDistance2WithoutAccess.toFixed(1) : "---",
      avgTime: d.averageTime && !props.isDistanceOnlyCalculationMethod ? d.averageTime.toFixed(1) : "---",
      avgTimeWith: d.averageTimeWithAccess && props.showWithAndWithoutTimeDistanceValues && !props.isDistanceOnlyCalculationMethod ? d.averageTimeWithAccess.toFixed(1) : "---",
      avgTimeWithout: d.averageTimeWithoutAccess && props.showWithAndWithoutTimeDistanceValues && !props.isDistanceOnlyCalculationMethod ? d.averageTimeWithoutAccess.toFixed(1) : "---",
      avgTime2: d.averageTime2 && !props.isDistanceOnlyCalculationMethod ? d.averageTime2.toFixed(1) : "---",
      avgTime2With: d.averageTime2WithAccess && props.showWithAndWithoutTimeDistanceValues && !props.isDistanceOnlyCalculationMethod ? d.averageTime2WithAccess.toFixed(1) : "---",
      avgTime2Without: d.averageTime2WithoutAccess && props.showWithAndWithoutTimeDistanceValues && !props.isDistanceOnlyCalculationMethod ? d.averageTime2WithoutAccess.toFixed(1) : "---",
      maxTime: d.maximumTime && !props.isDistanceOnlyCalculationMethod ? d.maximumTime.toFixed(1) : "---",
      maxTimeWith: d.maxTimeWithAccess && props.showWithAndWithoutTimeDistanceValues && !props.isDistanceOnlyCalculationMethod ? d.maxTimeWithAccess.toFixed(1) : "---",
      maxTimeWithout: d.maxTimeWithoutAccess && props.showWithAndWithoutTimeDistanceValues && !props.isDistanceOnlyCalculationMethod ? d.maxTimeWithoutAccess.toFixed(1) : "---",
      maxTime2: d.maximumTime2 && !props.isDistanceOnlyCalculationMethod ? d.maximumTime2.toFixed(1) : "---",
      maxTime2With: d.maxTime2WithAccess && props.showWithAndWithoutTimeDistanceValues && !props.isDistanceOnlyCalculationMethod ? d.maxTime2WithAccess.toFixed(1) : "---",
      maxTime2Without: d.maxTime2WithoutAccess && props.showWithAndWithoutTimeDistanceValues && !props.isDistanceOnlyCalculationMethod ? d.maxTime2WithoutAccess.toFixed(1) : "---",
    }));
    /* oxlint-enable sort-keys */
    return formattedData;
  };

  return (
    <AccordionContainer>
      <Accordion title="Zip Summaries" testId="zip-summaries-panel__zip-summaries-accordion">
        {
          !props.loading && tableData.length === 0 && (!comparedTableData || comparedTableData.length === 0) ?
            <h3 className="text-muted">There is no zip summaries data for this selection.</h3> :
            (
              <Waffles<ZipSummariesTableData>
                bulkActionBar={props.bulkActionBar}
                columns={generateColumnConfig()}
                customSort={customSort}
                compareData={comparedTableData}
                data={tableData}
                loading={props.loading} // Could this theoretically return true after load?
                fullWidth={true}
                rowDecorator={rowDecorator}
                testId="zip-summaries"
              />
            )
        }
      </Accordion>
    </AccordionContainer>
  );
};

const rowDecorator = (row: ZipSummariesTableData) => {
  const greenThreshold = 100;
  // if the value is "---" parseFloat returns NaN, which always returns false in comparisons
  const withPercent = parseFloat(row.membersWithAccessPercent);

  if (withPercent >= greenThreshold) {
    return "success";
  } else {
    if (isNaN(withPercent)) {
      return "gray danger";
    }
    return "danger";
  }
};

// This could be made generic and used elsewhere, but I don't know that we have other tables that are all-or-nothing like this
const populateWithBlankRows = (filledSummaries: ZipSummariesTableData[]) => {
  const blankSummaries: ZipSummariesTableData[] = [];
  const keys = Object.keys(filledSummaries[0]);
  keys.splice(keys.findIndex((k) => k === "key"), 1);
  filledSummaries.forEach((cd) => {
    // This has to be typed as Partial to get away with not instantiating every member of it
    const newBlank: Partial<ZipSummariesTableData> = {};
    keys.forEach((key) => {
      newBlank[key as keyof ZipSummariesTableData] = "---";
    });
    newBlank[wafflesKeyColumnName] = cd[wafflesKeyColumnName];
    newBlank.zipCode = cd.zipCode;
    // And now this has to be cast as not Partial so the array will take it
    blankSummaries.push(newBlank as ZipSummariesTableData);
  });

  return blankSummaries;
};

const customSort = (records: ZipSummariesTableData[], key: keyof ZipSummariesTableData, direction: SortDirections) => {
  const multiplier = direction === SortDirections.Asc ? 1 : -1;

  switch (key) {
    case "avgDist":
    case "avgDist2":
    case "avgDistWith":
    case "avgDist2With":
    case "avgDistWithout":
    case "avgDist2Without":
    case "avgTime":
    case "avgTime2":
    case "avgTimeWith":
    case "avgTime2With":
    case "avgTimeWithout":
    case "avgTime2Without":
    case "maxDist":
    case "maxDist2":
    case "maxDistWith":
    case "maxDist2With":
    case "maxDistWithout":
    case "maxDist2Without":
    case "maxTime":
    case "maxTime2":
    case "maxTimeWith":
    case "maxTime2With":
    case "maxTimeWithout":
    case "maxTime2Without":
    case "membersWithAccessCount":
    case "membersWithoutAccessCount":
    case "membersWithAccessPercent":
    case "membersWithoutAccessPercent":
    case "membershipCount":
    case "inRegionZipProviderCount":
      return numberSort(records, key, multiplier);
    case "city":
    case "zipCode":
      return records.sort((a, b) => (a[key] > b[key] ? 1 : -1) * multiplier);
    default:
      return records;
  }
};

export default ZipSummariesPanel;
